/**
 * Pop-out windows. A pop-out starts as a copy of a dashboard widget and then
 * keeps its own settings, so it can change ticker or interval without touching
 * the docked widget, and it stays open when the dashboard tab navigates away.
 */
import { profileService } from '../services/profileService';
import type { ToolWindow } from '../types/dashboard';

const POPOUT_PATH = /^\/popout\/([a-z0-9-]+)\/([^/]+)\/?$/;
const SEED_PREFIX = 'popout-seed:';
/** `frontend_state` key. Each pop-out has its own entry, so pop-outs do not overwrite each other. */
const STATE_KEY = 'popoutWindows';
const FEATURES = 'popup,width=1280,height=800';

export function isPopoutPath(pathname: string): boolean {
  return POPOUT_PATH.test(pathname);
}

export function popoutPath(tool: string, popoutId: string): string {
  return `/popout/${tool}/${encodeURIComponent(popoutId)}`;
}

function isToolWindow(value: unknown): value is ToolWindow {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string' && typeof record.type === 'string';
}

function parseWindow(text: unknown): ToolWindow | null {
  if (typeof text !== 'string') return null;
  try {
    const value: unknown = JSON.parse(text);
    return isToolWindow(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Open (or focus) the pop-out for one widget, starting from `start`.
 *
 * The starting copy goes through localStorage because the new window cannot
 * read this tab's memory, and the server copy may be seconds behind.
 */
export function openPopout(tool: string, popoutId: string, start: ToolWindow): void {
  const name = `popout-${tool}-${popoutId}`;
  const opened = window.open('', name, FEATURES);
  if (!opened) return;
  try {
    // Already showing this pop-out: bring it forward and keep its own settings.
    if (opened.location.pathname === popoutPath(tool, popoutId)) {
      opened.focus();
      return;
    }
  } catch {
    // A cross-origin page in that window. Load ours over it.
  }
  try {
    localStorage.setItem(SEED_PREFIX + popoutId, JSON.stringify(start));
  } catch {
    // Storage is full or blocked. The pop-out falls back to its saved copy.
  }
  opened.location.href = popoutPath(tool, popoutId);
}

/** The starting copy handed over by `openPopout`, read once. */
function takeSeed(popoutId: string): ToolWindow | null {
  try {
    const key = SEED_PREFIX + popoutId;
    const text = localStorage.getItem(key);
    localStorage.removeItem(key);
    return parseWindow(text);
  } catch {
    return null;
  }
}

/** A fresh hand-off from the dashboard wins. A reload reads the pop-out's own saved copy. */
export async function loadPopout(popoutId: string): Promise<ToolWindow | null> {
  const seeded = takeSeed(popoutId);
  if (seeded) {
    await savePopout(popoutId, seeded);
    return seeded;
  }
  const response = await profileService.getFrontendState();
  const entries: unknown = response.frontend_state?.[STATE_KEY];
  if (typeof entries !== 'object' || entries === null) return null;
  return parseWindow((entries as Record<string, unknown>)[popoutId]);
}

/**
 * Save one pop-out. The server deep-merges `frontend_state`, so a nested object
 * would keep keys we removed, such as a deleted drawing. A string replaces whole.
 */
export async function savePopout(popoutId: string, window: ToolWindow): Promise<void> {
  await profileService.saveFrontendState({ [STATE_KEY]: { [popoutId]: JSON.stringify(window) } });
}
